import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OnboardingGate from "@/components/auth/OnboardingGate";
import { useProfile } from "@/lib/profile/useProfile";
import { useEvents } from "@/lib/events/useEvents";
import { getApp } from "@/lib/apps";
import { CardGridSkeleton } from "@/components/common/Skeletons";
import EmptyState from "@/components/common/EmptyState";
import { CalendarDays } from "lucide-react";
import type { EventRow } from "@/lib/events/types";

const fmtWhen = (iso: string) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const EventCard = ({ ev }: { ev: EventRow }) => (
  <Link
    to={`/events/${ev.id}`}
    className="group flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card transition-colors hover:border-foreground/40"
  >
    <div className="aspect-[16/9] w-full bg-muted">
      {ev.cover_image_url ? (
        <img
          src={ev.cover_image_url}
          alt=""
          className="h-full w-full object-cover"
        />
      ) : null}
    </div>
    <div className="p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {fmtWhen(ev.starts_at)}
      </p>
      <h3 className="mt-1 text-base font-semibold group-hover:underline">{ev.title}</h3>
      {ev.location_text ? (
        <p className="mt-1 text-xs text-muted-foreground">{ev.location_text}</p>
      ) : null}
    </div>
  </Link>
);

const EventsInner = () => {
  const { profile, loading: pLoading } = useProfile();
  const app = profile?.active_app ?? null;
  const meta = useMemo(() => (app ? getApp(app) : null), [app]);

  const [scope, setScope] = useState<"upcoming" | "mine">("upcoming");
  const { events, loading } = useEvents({ app, scope });

  if (pLoading) {
    return (
      <main className="mx-auto max-w-6xl px-6 py-10">
        <CardGridSkeleton cards={6} cols={3} />
      </main>
    );
  }

  if (!app) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-12">
        <h1 className="text-2xl font-semibold">Pick a community first</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Visit your account to choose your active app, then come back to see events.
        </p>
        <Link
          to="/account"
          className="mt-6 inline-flex rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background"
        >
          Go to Account
        </Link>
      </main>
    );
  }

  return (
    <main className={`mx-auto max-w-6xl px-6 py-10 ${meta?.themeClass ?? ""}`}>
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {meta?.community} · Events
          </p>
          <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">
            Gatherings in {meta?.name}
          </h1>
        </div>
        <Link
          to="/events/new"
          className="rounded-full bg-foreground text-background px-4 py-2 text-xs font-medium hover:opacity-90 transition-opacity"
        >
          Host an event
        </Link>
      </header>

      <div className="mb-6 inline-flex rounded-full border border-border p-0.5 text-xs font-medium">
        {(["upcoming", "mine"] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setScope(s)}
            className={`rounded-full px-3 py-1 transition-colors ${
              scope === s
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {s === "upcoming" ? "Upcoming" : "Hosted by me"}
          </button>
        ))}
      </div>

      {loading ? (
        <CardGridSkeleton cards={6} cols={3} />
      ) : events.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title={
            scope === "mine"
              ? "You haven't hosted any events yet"
              : `No upcoming events in ${meta?.name}`
          }
          description={
            scope === "mine"
              ? "Host your first gathering — your community will see it here."
              : "Be the first to host. Even a small meet-up changes the room."
          }
          ctaLabel="Host an event"
          ctaHref="/events/new"
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {events.map((ev) => (
            <EventCard key={ev.id} ev={ev} />
          ))}
        </div>
      )}
    </main>
  );
};

const Events = () => (
  <div className="min-h-screen flex flex-col bg-background text-foreground">
    <SiteHeader />
    <OnboardingGate>
      <EventsInner />
    </OnboardingGate>
    <SiteFooter />
  </div>
);

export default Events;
